import React from 'react';
import {
  X as XIcon,
  ExternalLink,
  Mail,
  Phone,
  MapPin,
  Plus,
  FolderPlus,
  Info,
} from 'lucide-react';
import { AppSelect } from '../../../components/AppSelect';
import { AppSelectDropdown } from '../../../components/AppSelectDropdown';
import { formatDateToDDMMYYYY, formatDateTimeDDMMYYYY } from '../../../utils/date';
import { contactDisplayName } from '../../../types';
import { FALLBACK_ACTIVITY_TYPES } from '../../../utils/activityIcons';
import {
  leadSelectDropdownFields,
} from '../../../utils/leadSelectOption';
import { CreateProjectDocumentFlow } from '../../projects/components/CreateProjectDocumentFlow';
import { useModuleAccess } from '../../../hooks/useModuleAccess';
import { useCapabilities } from '../../../hooks/useCapabilities';
import { contactDetailPath } from '../../../utils/contactRoutes';
import { projectDetailPath } from '../../../utils/projectRoutes';
import {
  MAIL_STATUSES,
  getClassificationLabel,
  type UrgencyLevel,
  displayNameFromAddr,
  getCounterpartyAddr,
  extractEmail,
  initialsOf,
  isUrgent,
  type InboxEmail,
  type InboxLeadActivity,
  type LeadOption,
  type MailStatus,
} from '../inboxShared';
import { InlineLoadingState } from '../../../components/LoadingSpinner';

interface InboxContextPanelProps {
  selectedMail: InboxEmail;
  accountEmail: string;
  anfLabel: string | null;
  linkedLead: LeadOption | null;
  statusValue: MailStatus;
  onChangeStatus: (s: MailStatus) => void;
  onChangeUrgency: (level: Exclude<UrgencyLevel, null>) => void;
  onAssignUser: (userId: string | null) => void;
  members: { id: string; label: string }[];
  updatingStatus: boolean;
  leadsForLink: LeadOption[];
  onLink: (leadId: string | number | null) => void;
  onCreateLead: () => void;
  linking: boolean;
  onOpenLead: () => void;
  onOpenActivity?: (activity: InboxLeadActivity) => void;
  onOpenContact: () => void;
  onCreateActivity?: () => void;
  activitiesRefreshKey?: number;
  onClose?: () => void;
  categories?: { id: string; label: string }[];
  onToggleCategory?: (folderExternalId: string, assigned: boolean) => void;
}

/** Zwei Schriftgrößen für die ganze Spalte: `text-sm` trägt Namen und Titel, `text-xs` alles
 *  Begleitende (Labels, Meta, Buttons). Zwischengrößen wie 10/11px machen die Spalte unruhig. */
const PANEL_TITLE_CLASS = 'text-sm font-semibold';
const PANEL_ACTION_CLASS = 'text-xs font-medium';

/** Bewusst ohne Caps und Bold — Gruppentitel führen über Größe und Farbe, nicht über Gewicht. */
const SECTION_LABEL_CLASS = 'text-xs font-semibold text-app-text-muted';

function SectionLabel({ children }: { children: React.ReactNode }) {
  return <div className={`mb-2 ${SECTION_LABEL_CLASS}`}>{children}</div>;
}

function formatEuro(value: number | null | undefined): string {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(Number(value));
}

function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3 py-2 border-b border-app-border last:border-0">
      <span className="text-xs text-app-text-subtle shrink-0">{label}</span>
      <span className="text-xs text-app-text text-right min-w-0">{children}</span>
    </div>
  );
}

type ActivityTypeOption = { id: string; label: string; icon: string };

const INBOX_ACTIVITIES_PREVIEW = 8;

function getActivityTypeLabel(typeId: string, types: ActivityTypeOption[]): string {
  return types.find((t) => t.id === typeId)?.label ?? typeId;
}

function activityAssigneeLabel(activity: InboxLeadActivity): string | null {
  const u = activity.assignee_users?.[0];
  if (u?.display_name?.trim()) return u.display_name.trim();
  if (u?.email?.trim()) return u.email.trim();
  const e = activity.assignee_employees?.[0];
  if (e) {
    const name = `${e.first_name ?? ''} ${e.last_name ?? ''}`.trim();
    if (name) return name;
  }
  return null;
}

function activityWhenLabel(activity: InboxLeadActivity): string | null {
  if (activity.due_at) return formatDateTimeDDMMYYYY(activity.due_at);
  return null;
}

function ContactDetailLine({
  icon: Icon,
  href,
  children,
  className = 'text-app-text-muted',
  wrap = false,
}: {
  icon: React.ElementType;
  href?: string;
  children: React.ReactNode;
  className?: string;
  /** Multi-line wrap for long addresses (email/phone stay single-line truncated). */
  wrap?: boolean;
}) {
  const textClass = wrap
    ? `block text-xs leading-snug break-words line-clamp-2 ${className}`
    : `block text-xs truncate ${className}`;
  const content = href ? (
    <a href={href} className={`hover:underline ${textClass}`}>
      {children}
    </a>
  ) : (
    <span className={textClass}>{children}</span>
  );
  return (
    <div className="flex items-start gap-2.5 min-w-0">
      <Icon className="w-4 h-4 text-app-text-subtle shrink-0 mt-0.5" />
      <div className="min-w-0 flex-1 overflow-hidden">{content}</div>
    </div>
  );
}

export function InboxContextPanel({
  selectedMail,
  accountEmail,
  anfLabel,
  linkedLead,
  statusValue,
  onChangeStatus,
  onChangeUrgency,
  onAssignUser,
  members,
  updatingStatus,
  leadsForLink,
  onLink,
  onCreateLead,
  linking,
  onOpenLead,
  onOpenActivity,
  onOpenContact,
  onCreateActivity,
  activitiesRefreshKey = 0,
  onClose,
  categories = [],
  onToggleCategory,
}: InboxContextPanelProps) {
  const { canOpen } = useModuleAccess();
  const { can } = useCapabilities();
  // Projektverknüpfung nur mit Leserecht auf Projekte; neu anlegen nur mit Bearbeitungsrecht.
  const canViewProjects = can('web.projekte.view');
  const canCreateProject = can('web.projekte.edit');
  const [emailActivities, setEmailActivities] = React.useState<InboxLeadActivity[]>([]);
  const [leadActivities, setLeadActivities] = React.useState<InboxLeadActivity[]>([]);
  const [emailActivitiesLoading, setEmailActivitiesLoading] = React.useState(false);
  const [leadActivitiesLoading, setLeadActivitiesLoading] = React.useState(false);
  const activitiesLoading = emailActivitiesLoading || leadActivitiesLoading;
  const [activityTypes, setActivityTypes] = React.useState<ActivityTypeOption[]>(FALLBACK_ACTIVITY_TYPES);

  React.useEffect(() => {
    let cancelled = false;
    fetch('/api/settings/activity-types', { credentials: 'include' })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (cancelled || !Array.isArray(data) || data.length === 0) return;
        setActivityTypes(
          data.map((t: { id?: string; label?: string; icon?: string }) => ({
            id: String(t.id ?? ''),
            label: String(t.label ?? t.id ?? ''),
            icon: String(t.icon ?? 'task'),
          })).filter((t) => t.id),
        );
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const activityLeadId = linkedLead?.id ?? selectedMail.lead_id ?? null;
  const filedCategoryId = (selectedMail.provider_folder_ids ?? []).find((id) => categories.some((category) => category.id === id)) ?? '';
  const pendingCategoryId = categories.some((category) => category.id === selectedMail.pending_folder_id)
    ? String(selectedMail.pending_folder_id)
    : '';

  React.useEffect(() => {
    const emailId = selectedMail?.id;
    if (emailId == null || emailId === '') {
      setEmailActivities([]);
      setEmailActivitiesLoading(false);
      return;
    }
    const ac = new AbortController();
    setEmailActivitiesLoading(true);
    fetch(`/api/inbox/emails/${encodeURIComponent(String(emailId))}/tasks`, {
      credentials: 'include',
      signal: ac.signal,
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        const tasks = Array.isArray(data?.tasks) ? data.tasks : [];
        const activities = tasks
          .map((t: { activity?: InboxLeadActivity }) => t.activity)
          .filter((a): a is InboxLeadActivity => Boolean(a?.id));
        setEmailActivities(activities);
      })
      .catch(() => {
        if (!ac.signal.aborted) setEmailActivities([]);
      })
      .finally(() => {
        if (!ac.signal.aborted) setEmailActivitiesLoading(false);
      });
    return () => ac.abort();
  }, [selectedMail.id, activitiesRefreshKey]);

  React.useEffect(() => {
    const leadId = canViewProjects ? activityLeadId : null;
    if (leadId == null || leadId === '') {
      setLeadActivities([]);
      setLeadActivitiesLoading(false);
      return;
    }
    const ac = new AbortController();
    setLeadActivitiesLoading(true);
    fetch(`/api/leads/${encodeURIComponent(String(leadId))}/activities`, {
      credentials: 'include',
      signal: ac.signal,
    })
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => {
        setLeadActivities(Array.isArray(data) ? (data as InboxLeadActivity[]) : []);
      })
      .catch(() => {
        if (!ac.signal.aborted) setLeadActivities([]);
      })
      .finally(() => {
        if (!ac.signal.aborted) setLeadActivitiesLoading(false);
      });
    return () => ac.abort();
  }, [activityLeadId, activitiesRefreshKey, canViewProjects]);

  const displayActivities = React.useMemo(() => {
    const map = new Map<string, InboxLeadActivity>();
    for (const a of leadActivities) map.set(a.id, a);
    for (const a of emailActivities) map.set(a.id, a);
    return [...map.values()].sort((a, b) => {
      const ta = a.due_at ? new Date(a.due_at).getTime() : Number.MAX_SAFE_INTEGER;
      const tb = b.due_at ? new Date(b.due_at).getTime() : Number.MAX_SAFE_INTEGER;
      if (ta !== tb) return ta - tb;
      return (a.title || '').localeCompare(b.title || '');
    });
  }, [leadActivities, emailActivities]);

  const counterpartyAddr = getCounterpartyAddr(selectedMail, accountEmail);
  const mailContactName = displayNameFromAddr(counterpartyAddr);
  const mailContactEmail = extractEmail(counterpartyAddr);

  const leadLocation =
    linkedLead?.site_address?.trim() ||
    linkedLead?.address?.trim() ||
    null;

  const projectSelectOptions = React.useMemo(
    () => [
      { value: '', label: 'Kein Projekt' },
      ...leadsForLink.map((l) => ({
        value: String(l.id),
        ...leadSelectDropdownFields(l),
      })),
    ],
    [leadsForLink],
  );

  const crmContact = linkedLead?.contact;
  const contactName = crmContact ? contactDisplayName(crmContact) || linkedLead?.name || mailContactName : mailContactName;
  const contactEmail = (crmContact?.email || linkedLead?.email || mailContactEmail || '').trim();
  const contactPhone = (crmContact?.phone || linkedLead?.phone || '').trim();
  const contactAddress = (crmContact?.address || linkedLead?.address || '').trim();
  const contactRole =
    (crmContact?.contact_type || crmContact?.contact_kind || '').trim() || null;
  const contactId = linkedLead?.contact_id ?? crmContact?.id ?? null;
  const projectOpenable = linkedLead != null && canOpen(projectDetailPath({ id: linkedLead.id }));
  const contactOpenable = contactId != null && canOpen(contactDetailPath(contactId));
  const contactInitials = initialsOf(contactName || counterpartyAddr);

  const hasActivityList = !activitiesLoading && displayActivities.length > 0;

  const linkScopeHint = selectedMail.thread_id
    ? 'Zuweisung gilt für den gesamten E-Mail-Thread.'
    : 'Zuweisung gilt nur für diese einzelne Nachricht.';

  const openActivity = (activity: InboxLeadActivity) => {
    if (onOpenActivity) {
      onOpenActivity(activity);
      return;
    }
    onOpenLead();
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-app-surface">
      {onClose && (
        <div className="flex items-center justify-end px-3 py-2 border-b border-app-border bg-app-surface md:hidden">
          <button type="button" onClick={onClose} className="p-1.5 rounded text-app-text-subtle hover:bg-app-hover" aria-label="Schließen">
            <XIcon className="w-5 h-5" />
          </button>
        </div>
      )}

      <div className="app-scrollbar min-h-0 flex-1 space-y-5 overflow-y-auto px-[18px] py-5">
        <div>
          <SectionLabel>Dringlichkeit</SectionLabel>
          <AppSelect
            value={
              selectedMail.urgency_level === 'high' || selectedMail.urgency_level === 'low' || selectedMail.urgency_level === 'normal'
                ? selectedMail.urgency_level
                : isUrgent(selectedMail)
                  ? 'high'
                  : 'normal'
            }
            disabled={updatingStatus}
            onChange={(value) => {
              if (value === 'high' || value === 'normal' || value === 'low') onChangeUrgency(value);
            }}
            options={[
              { value: 'high', label: 'hoch' },
              { value: 'normal', label: 'normal' },
              { value: 'low', label: 'niedrig' },
            ]}
            triggerClassName="!min-h-0 h-9 !py-0"
            aria-label="Dringlichkeit"
          />
        </div>
        {categories.length > 0 ? (
          <div>
            <SectionLabel>Ordner</SectionLabel>
            <AppSelect
              value={filedCategoryId || pendingCategoryId}
              onChange={(nextId) => {
                const currentId = filedCategoryId || pendingCategoryId;
                // Moving clears the pending folder on the server, so only "Keiner" has to drop it.
                if (currentId && currentId !== nextId && (filedCategoryId || !nextId)) onToggleCategory?.(currentId, false);
                if (nextId && nextId !== filedCategoryId) onToggleCategory?.(nextId, true);
              }}
              options={[
                { value: '', label: 'Keiner' },
                ...categories.map((category) => ({
                  value: category.id,
                  label: !filedCategoryId && category.id === pendingCategoryId ? `${category.label} (bei Erledigt)` : category.label,
                })),
              ]}
              triggerClassName="!min-h-0 h-9 !py-0"
              aria-label="Ordner"
            />
          </div>
        ) : null}
        <div>
          <SectionLabel>Status</SectionLabel>
          <AppSelect
            value={statusValue}
            disabled={updatingStatus}
            onChange={(v) => onChangeStatus(v as MailStatus)}
            options={MAIL_STATUSES.map((s) => ({ value: s, label: getClassificationLabel(s) }))}
            triggerClassName="!min-h-0 h-9 !py-0"
            aria-label="Status"
          />
        </div>
        <div>
          <SectionLabel>Zugewiesen</SectionLabel>
          <AppSelectDropdown
            value={selectedMail.assigned_user_id ?? ''}
            onChange={(value) => onAssignUser(value || null)}
            searchable
            searchPlaceholder="Nutzer suchen"
            placeholder="Nicht zugewiesen"
            disabled={updatingStatus}
            className="w-full"
            triggerClassName="!min-h-0 h-9 !py-0"
            options={[
              { value: '', label: 'Nicht zugewiesen' },
              ...members.map((member) => ({ value: member.id, label: member.label })),
            ]}
          />
        </div>

        {canViewProjects && (
        <div>
          {/* Reichweite der Zuweisung als Hover-Hinweis — sie ändert sich selten und muss
              nicht dauerhaft zwei Zeilen belegen. */}
          <div className="mb-2 flex items-center gap-1.5">
            <span className={SECTION_LABEL_CLASS}>Verknüpftes Projekt</span>
            <span className="inline-flex text-app-text-subtle" tabIndex={0} title={linkScopeHint}>
              <Info className="h-3.5 w-3.5" aria-hidden />
              <span className="sr-only">{linkScopeHint}</span>
            </span>
          </div>
          <AppSelectDropdown
            value={linkedLead ? String(linkedLead.id) : ''}
            onChange={(v) => onLink(v ? v : null)}
            searchable
            searchPlaceholder="Projekt suchen"
            placeholder="Kein Projekt"
            disabled={linking}
            className="w-full"
            onCreate={canCreateProject ? onCreateLead : undefined}
            createLabel="Neues Projekt erstellen"
            createIcon={<FolderPlus className="h-4 w-4" />}
            options={projectSelectOptions}
          />
          {linkedLead ? (
            // Das Dropdown zeigt Titel und Kunde bereits — darunter nur noch die Kennzahlen, ohne
            // eigene Karte und ohne wiederholten Kopf.
            <div className="mt-1">
              <div>
                {linkedLead.status && (
                  <DetailRow label="Status">
                    <span className="inline-flex items-center gap-1.5 font-medium text-pipedrive-blue">
                      <span className="w-1.5 h-1.5 rounded-full bg-pipedrive-blue shrink-0" aria-hidden />
                      {linkedLead.status}
                    </span>
                  </DetailRow>
                )}
                <DetailRow label="Auftragswert">
                  <span className="font-semibold text-app-text">{formatEuro(linkedLead.net_total)}</span>
                </DetailRow>
                {linkedLead.created_at && (
                  <DetailRow label="Erstellt am">
                    {formatDateToDDMMYYYY(linkedLead.created_at.slice(0, 10))}
                  </DetailRow>
                )}
                {leadLocation && <DetailRow label="Baustelle">{leadLocation}</DetailRow>}
              </div>

              {projectOpenable && (
                <button
                  type="button"
                  onClick={onOpenLead}
                  className={`mt-3 w-full inline-flex items-center justify-center gap-1.5 rounded-lg bg-pipedrive-blue py-1.5 text-white transition-colors hover:bg-blue-700 ${PANEL_ACTION_CLASS}`}
                >
                  <ExternalLink className="w-4 h-4" />
                  Projekt öffnen
                </button>
              )}
              {projectOpenable && (
                <CreateProjectDocumentFlow
                  projectRouteId={String(linkedLead.id)}
                  buttonClassName={`mt-1.5 w-full py-1 text-center text-pipedrive-blue hover:text-blue-700 ${PANEL_ACTION_CLASS}`}
                />
              )}
            </div>
          ) : null}
        </div>
        )}

        {onCreateActivity && (
          <div>
            <div className="mb-2 flex items-center justify-between gap-2">
              <div className={SECTION_LABEL_CLASS}>
                Aktivitäten
                {hasActivityList ? ` (${displayActivities.length})` : ''}
              </div>
              {hasActivityList && (
                <button
                  type="button"
                  onClick={onCreateActivity}
                  title="Aktivität planen"
                  className={`inline-flex shrink-0 items-center gap-1 text-pipedrive-blue hover:text-blue-700 ${PANEL_ACTION_CLASS}`}
                >
                  <Plus className="h-3.5 w-3.5" />
                  Aktivität
                </button>
              )}
            </div>
            {hasActivityList ? (
              <div>
                <ul className="divide-y divide-app-border">
                  {displayActivities.slice(0, INBOX_ACTIVITIES_PREVIEW).map((activity) => {
                    const typeLabel = getActivityTypeLabel(activity.activity_type, activityTypes);
                    const when = activityWhenLabel(activity);
                    const assignee = activityAssigneeLabel(activity);
                    return (
                      <li key={activity.id}>
                        <button
                          type="button"
                          onClick={() => openActivity(activity)}
                          className="-mx-2 w-[calc(100%+1rem)] rounded-md px-2 py-2 text-left transition-colors hover:bg-app-hover/80"
                        >
                          <span className="block min-w-0">
                            <span
                              className={`block truncate ${PANEL_TITLE_CLASS} ${
                                activity.done ? 'text-app-text-subtle line-through' : 'text-app-text'
                              }`}
                            >
                              {activity.title?.trim() || typeLabel}
                            </span>
                            {/* Eine Meta-Zeile statt zwei gleich aussehender Untertitel. */}
                            <span className="mt-0.5 block truncate text-xs text-app-text-subtle">
                              {typeLabel}
                              {activity.done ? ' · Erledigt' : ' · Offen'}
                              {when ? ` · ${when}` : ''}
                              {assignee ? ` · ${assignee}` : ''}
                            </span>
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
                {linkedLead && projectOpenable && leadActivities.length > INBOX_ACTIVITIES_PREVIEW && (
                  <button
                    type="button"
                    onClick={onOpenLead}
                    className={`w-full border-t border-app-border py-2 text-left text-pipedrive-blue hover:text-blue-700 ${PANEL_ACTION_CLASS}`}
                  >
                    Alle {leadActivities.length} Aktivitäten im Projekt
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                {activitiesLoading ? (
                  <InlineLoadingState label="Aktivitäten werden geladen…" className="text-xs" />
                ) : (
                  <>
                    <p className="text-xs text-app-text-subtle">
                      {linkedLead
                        ? 'Noch keine Aktivitäten für diese E-Mail oder dieses Projekt.'
                        : 'Noch keine Aktivitäten für diese E-Mail — Sie können trotzdem eine anlegen.'}
                    </p>
                    <button
                      type="button"
                      onClick={onCreateActivity}
                      className={`flex w-full items-center gap-1 py-1 text-left text-pipedrive-blue hover:text-blue-700 ${PANEL_ACTION_CLASS}`}
                    >
                      <Plus className="h-3.5 w-3.5" />
                      Aktivität aus E-Mail planen
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        <div>
          <SectionLabel>Kontakt</SectionLabel>
          <div className="min-w-0 overflow-hidden">
            <div className="flex items-center gap-3">
              <div
                className="flex items-center justify-center w-10 h-10 rounded-full bg-orange-100 text-orange-700 font-semibold text-sm shrink-0"
                aria-hidden
              >
                {contactInitials}
              </div>
              <div className="min-w-0 flex-1">
                <p className={`truncate text-app-text ${PANEL_TITLE_CLASS}`}>{contactName}</p>
                {contactRole && <p className="text-xs text-app-text-subtle mt-0.5">{contactRole}</p>}
              </div>
            </div>

            <div className="mt-3 space-y-2.5">
              {contactEmail && (
                <ContactDetailLine icon={Mail} href={`mailto:${contactEmail}`} className="text-pipedrive-blue font-medium">
                  {contactEmail}
                </ContactDetailLine>
              )}
              {contactPhone && (
                <ContactDetailLine icon={Phone} href={`tel:${contactPhone.replace(/\s/g, '')}`}>
                  {contactPhone}
                </ContactDetailLine>
              )}
              {contactAddress && (
                <ContactDetailLine icon={MapPin} wrap>
                  {contactAddress}
                </ContactDetailLine>
              )}
              {!contactEmail && !contactPhone && !contactAddress && (
                <p className="text-xs text-app-text-subtle">Keine Kontaktdaten hinterlegt — nur Absender der E-Mail bekannt.</p>
              )}
            </div>

            {contactOpenable && (
              <button
                type="button"
                onClick={onOpenContact}
                className={`mt-3 py-1 text-left text-pipedrive-blue hover:text-blue-700 ${PANEL_ACTION_CLASS}`}
              >
                Kontakt öffnen
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
